import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, Input, Link, PasswordDisplayIcon, PasswordHiddenIcon, ProfileIcon } from "../../components";
import { LoginContainer } from "./LoginContainer";
import { AuthorizeCallback, SSOLogin } from "../../services/oauth";
import { genericValidation } from "../../validations/validations";
import { PasswordResetRequiredError } from "../../models/PasswordResetRequiredError";
import { authLoginURL } from "../../constants/urls";

export const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [forgottenPasswordPagePath, setForgottenPasswordPagePath] = useState("");
  const [clearValue, setClearValue] = useState<boolean | undefined>(undefined);
  const [triggerFocus, setTriggerFocus] = useState<boolean | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as {
    showForgottenPasswordMessage: boolean;
    showResetPasswordMessage: boolean;
    redirectUri: string;
  };
  const [params] = useSearchParams();
  const callback = params.get("callback") ?? null;
  const error = params.get("error") ?? null;
  const enableSSO = process.env.REACT_APP_IS_SSO_ENABLED?.toLowerCase() ?? "false";
  const isEnableForgottenPassword = process.env.REACT_APP_ENABLE_FORGOTTEN_PASSWORD?.toLowerCase() ?? false;

  window.addEventListener("pageshow", () => {
    setUsername("");
    setPassword("");
    setClearValue(!clearValue);
    setTriggerFocus(!triggerFocus);
  });

  useEffect(() => {
    if (callback !== null) {
      setForgottenPasswordPagePath(`/forgotten-password?redirect_uri=${location.pathname}${location.search}`);
      if (locationState?.showForgottenPasswordMessage === true) {
        navigate(`${location.pathname}${location.search}`, { replace: true });
        toast.info(t("forgottenPasswordMessage") as string);
      }
      if (locationState?.showResetPasswordMessage === true) {
        navigate(`${location.pathname}${location.search}`, { replace: true });
        toast.info(t("resetPasswordComplete") as string);
      }
      if (error) {
        toast.error(error);
      }
    } else {
      navigate("/");
    }
    // eslint-disable-next-line
  }, []);

  const onIconClickHandler = useCallback(() => {
    setShowPassword(!showPassword);
  }, [showPassword, setShowPassword]);

  const usernameValidationMsg = genericValidation(t("loginUsername"), username, t);

  const isUserNotValidated = !(username && password) || usernameValidationMsg !== null;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    try {
      await AuthorizeCallback(username, password, t).then((result) => {
        if (result === false) {
          setLoading(false);
        }
        return;
      });
    } catch (error) {
      if (error instanceof PasswordResetRequiredError && callback !== null) {
        navigate(`/set-password?redirect_uri=/login%3fcallback=${callback}&reset_token=${error.message}`);
      } else {
        setLoading(false);
        throw error;
      }
    }
  };

  const handleSSO = async () => {
    if (callback) {
      if (!callback.startsWith(authLoginURL)) {
        toast.error(t("unknownAuthenticationServer") as string);
        return false;
      } else {
        try {
          await SSOLogin(callback, "state", t);
        } catch (error) {
          toast.error(t("errorToRedirectSSO") as string);
        }
      }
    } else {
      toast.error(t("noCallbackProvided") as string);
      return false;
    }
  };

  return (
    <LoginContainer>
      <form className="login-elements" onSubmit={handleSubmit}>
        <Input
          placeholder={t("loginUsername")}
          onChange={(e) => setUsername(e.target.value)}
          autofocus={true}
          validationMsg={usernameValidationMsg}
          value={username}
          className="username-input"
          Icon={ProfileIcon}
          triggerFocus={triggerFocus}
          clearValue={clearValue}
        />
        <Input
          placeholder={t("loginPassword")}
          onChange={(e) => setPassword(e.target.value)}
          type={showPassword ? "text" : "password"}
          autofocus={false}
          className="username-input"
          Icon={showPassword ? PasswordHiddenIcon : PasswordDisplayIcon}
          onIconClick={onIconClickHandler}
          clearValue={clearValue}
        />
        <Button
          lKey={t("loginButtonText")}
          isDisabled={isUserNotValidated}
          className="username-button"
          type="submit"
          isLoading={loading}
        />
        {
          isEnableForgottenPassword &&
          <Link className="link-text" isPointer={true} lKey={t("loginLinkText")} path={forgottenPasswordPagePath} />
        }
        {enableSSO === "true" && (
          <Button
            lKey={t("loginSSOButtonText")}
            isDisabled={false}
            onClick={handleSSO}
            className="sso-button"
            type="button"
          />
        )}
      </form>
    </LoginContainer>
  );
};
